
import React, { useMemo, useState } from "react";
import {
  Database,
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Tags,
  MapPin,
  GraduationCap,
  Heart,
  BriefcaseBusiness,
  Utensils,
} from "lucide-react";

const masterCategories = [
  { name: "Gender", icon: UsersIcon(), values: ["Male", "Female"] },
  { name: "Marital Status", values: ["Never Married", "Divorced", "Widowed", "Awaiting Divorce"] },
  { name: "Religion", values: ["Hindu", "Christian", "Muslim", "Other"] },
  { name: "Caste", values: ["Kongu Vellalar", "Other"] },
  { name: "Sub Caste", values: ["Vellala Gounder", "Nattu Gounder", "Poosari Gounder"] },
  { name: "Mother Tongue", values: ["Tamil", "English", "Telugu", "Kannada"] },
  { name: "Education", values: ["B.E.", "B.Tech.", "M.B.A.", "M.C.A.", "M.Sc.", "Diploma", "12th"] },
  { name: "Occupation", values: ["Software Engineer", "Doctor", "Teacher", "Business", "Government Employee"] },
  { name: "Annual Income", values: ["Below ₹2 Lakhs", "₹2–5 Lakhs", "₹5–10 Lakhs", "Above ₹10 Lakhs"] },
  { name: "Country", values: ["India", "United States", "United Kingdom", "Canada", "Australia"] },
  { name: "State", values: ["Tamil Nadu", "Kerala", "Karnataka", "Andhra Pradesh"] },
  { name: "District", values: ["Salem", "Erode", "Coimbatore", "Namakkal", "Tiruppur"] },
  { name: "Taluk", values: ["Salem", "Mettur", "Erode", "Bhavani", "Gobichettipalayam"] },
  { name: "Star", values: ["Ashwini", "Bharani", "Karthigai", "Rohini", "Mrigashirsham"] },
  { name: "Raasi", values: ["Mesham", "Rishabam", "Mithunam", "Kadagam", "Simmam"] },
  { name: "Dosham", values: ["No Dosham", "Chevvai Dosham", "Rahu Kethu Dosham"] },
  { name: "Body Type", values: ["Slim", "Average", "Athletic", "Heavy"] },
  { name: "Complexion", values: ["Fair", "Wheatish", "Medium", "Dark"] },
  { name: "Food Habit", values: ["Vegetarian", "Non-Vegetarian", "Eggetarian"] },
  { name: "Hobbies", values: ["Reading", "Music", "Travel", "Cooking", "Sports"] },
];

function UsersIcon() {
  return <Heart size={16} />;
}

function makeInitialMasters() {
  let id = 1;

  return masterCategories.reduce((result, category) => {
    result[category.name] = category.values.map((value) => ({
      id: id++,
      value,
      status: "Active",
    }));

    return result;
  }, {});
}

function StatusBadge({ status }) {
  const active = status === "Active";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${active
        ? "bg-emerald-50 text-emerald-700"
        : "bg-slate-100 text-slate-600"
        }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"
          }`}
      />
      {status}
    </span>
  );
}

function ValueModal({ category, item, onClose, onSave }) {
  const [value, setValue] = useState(item?.value || "");
  const [error, setError] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!value.trim()) {
      setError("Please enter a master value.");
      return;
    }

    onSave(value.trim());
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              {item ? "Edit Master Value" : "Add Master Value"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Category: {category}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5">
            <label className="text-xs font-semibold text-slate-600">
              Master Value *
            </label>

            <input
              autoFocus
              required
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                setError("");
              }}
              placeholder={`Enter ${category.toLowerCase()}`}
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />

            {error && (
              <p role="alert" className="mt-2 text-xs text-rose-600">
                {error}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 p-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-600"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white hover:bg-blue-700"
            >
              {item ? "Save Changes" : "Add Value"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Masters() {
  const [masters, setMasters] = useState(makeInitialMasters);
  const [selectedCategory, setSelectedCategory] = useState("Gender");
  const [searchCategory, setSearchCategory] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [notice, setNotice] = useState("");

  const selectedItems = masters[selectedCategory];

  const filteredCategories = masterCategories.filter((category) =>
    category.name.toLowerCase().includes(searchCategory.trim().toLowerCase())
  );

  const filteredItems = useMemo(() => {
    const term = searchValue.trim().toLowerCase();

    return selectedItems.filter((item) => {
      const matchesSearch = item.value.toLowerCase().includes(term);

      return (
        matchesSearch &&
        (!statusFilter || item.status === statusFilter)
      );
    });
  }, [selectedItems, searchValue, statusFilter]);

  const activeCount = selectedItems.filter(
    (item) => item.status === "Active"
  ).length;

  const inactiveCount = selectedItems.length - activeCount;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredItems.length / pageSize)
  );

  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;

  const visibleItems = filteredItems.slice(
    startIndex,
    startIndex + pageSize
  );

  const selectCategory = (name) => {
    setSelectedCategory(name);
    setSearchValue("");
    setStatusFilter("");
    setPage(1);
    setEditingItem(null);
  };

  const resetFilters = () => {
    setSearchValue("");
    setStatusFilter("");
    setPage(1);
  };

  const saveItem = (value) => {
    const duplicate = selectedItems.some(
      (item) =>
        item.value.toLowerCase() === value.toLowerCase() &&
        item.id !== editingItem?.id
    );

    if (duplicate) {
      setNotice(`"${value}" already exists in ${selectedCategory}.`);
      return;
    }

    if (editingItem) {
      setMasters((current) => ({
        ...current,
        [selectedCategory]: current[selectedCategory].map((item) =>
          item.id === editingItem.id ? { ...item, value } : item
        ),
      }));

      setNotice("Master value updated successfully.");
    } else {
      setMasters((current) => ({
        ...current,
        [selectedCategory]: [
          {
            id: Date.now(),
            value,
            status: "Active",
          },
          ...(current[selectedCategory] || []),
        ],
      }));

      setNotice("Master value added successfully.");
      setPage(1);
    }

    setModalOpen(false);
    setEditingItem(null);
  };

  const toggleStatus = (item) => {
    const nextStatus =
      item.status === "Active" ? "Inactive" : "Active";

    setMasters((current) => ({
      ...current,
      [selectedCategory]: current[selectedCategory].map((row) =>
        row.id === item.id ? { ...row, status: nextStatus } : row
      ),
    }));

    setNotice(`${item.value} is now ${nextStatus.toLowerCase()}.`);
  };

  const confirmDelete = () => {
    setMasters((current) => ({
      ...current,
      [selectedCategory]: current[selectedCategory].filter(
        (item) => item.id !== deleteItem.id
      ),
    }));

    setNotice("Master value deleted from the sample list.");
    setDeleteItem(null);
  };

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
          <span>Home</span>
          <span>/</span>
          <span>Management</span>
          <span>/</span>
          <span className="font-medium text-blue-600">Masters</span>
        </div>

        <h1 className="text-xl font-bold text-slate-800 sm:text-2xl">
          Masters Management
        </h1>

        <p className="mt-1 text-xs text-slate-500 sm:text-sm">
          Manage the dropdown values used in matrimonial profiles and searches.
        </p>
      </div>

      {/* Notification */}
      {notice && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice("")}
            aria-label="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Master Categories</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">
                {masterCategories.length}
              </h3>
              <p className="mt-1 text-[10px] text-slate-400">
                Available categories
              </p>
            </div>
            <div className="rounded-xl bg-blue-50 p-3 text-blue-600">
              <Database size={22} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Values in Category</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">
                {selectedItems.length}
              </h3>
              <p className="mt-1 text-[10px] text-slate-400">
                {selectedCategory}
              </p>
            </div>
            <div className="rounded-xl bg-violet-50 p-3 text-violet-600">
              <Tags size={22} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Active Values</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">
                {activeCount}
              </h3>
              <p className="mt-1 text-[10px] text-slate-400">
                {inactiveCount} inactive
              </p>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3 text-emerald-600">
              <CheckCircle2 size={22} />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[250px_minmax(0,1fr)]">
        {/* Category sidebar */}
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4">
            <h2 className="text-sm font-bold text-slate-800">
              Master Categories
            </h2>
            <p className="mt-1 text-[11px] text-slate-400">
              Select a category to manage.
            </p>

            <div className="relative mt-4">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={searchCategory}
                onChange={(e) => setSearchCategory(e.target.value)}
                placeholder="Search categories"
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-blue-400"
              />
            </div>
          </div>

          <nav className="max-h-[600px] space-y-1 overflow-y-auto p-2">
            {filteredCategories.map((category) => {
              const selected = selectedCategory === category.name;
              const count = masters[category.name]?.length || 0;

              return (
                <button
                  key={category.name}
                  type="button"
                  onClick={() => selectCategory(category.name)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition ${selected
                    ? "bg-blue-50 font-semibold text-blue-700"
                    : "text-slate-600 hover:bg-slate-50"
                    }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${selected
                      ? "bg-blue-100 text-blue-600"
                      : "bg-slate-100 text-slate-500"
                      }`}
                  >
                    {category.name === "Country" ||
                      category.name === "State" ||
                      category.name === "District" ||
                      category.name === "Taluk" ? (
                      <MapPin size={15} />
                    ) : category.name === "Education" ? (
                      <GraduationCap size={15} />
                    ) : category.name === "Occupation" ? (
                      <BriefcaseBusiness size={15} />
                    ) : category.name === "Food Habit" ? (
                      <Utensils size={15} />
                    ) : category.name === "Marital Status" ? (
                      <Heart size={15} />
                    ) : (
                      <Database size={15} />
                    )}
                  </span>

                  <span className="min-w-0 flex-1 text-xs">
                    {category.name}
                  </span>

                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[10px] ${selected
                      ? "bg-blue-100 text-blue-700"
                      : "bg-slate-100 text-slate-500"
                      }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}

            {filteredCategories.length === 0 && (
              <p className="px-3 py-8 text-center text-xs text-slate-400">
                No categories found.
              </p>
            )}
          </nav>
        </section>

        {/* Values table */}
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div>
              <h2 className="text-sm font-bold text-slate-800">
                {selectedCategory}
              </h2>
              <p className="mt-1 text-[11px] text-slate-400">
                Manage values for this master category.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setEditingItem(null);
                setModalOpen(true);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white hover:bg-blue-700"
            >
              <Plus size={16} />
              Add Value
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-4 sm:grid-cols-2">
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setPage(1);
                }}
                placeholder={`Search ${selectedCategory.toLowerCase()} values`}
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-blue-400"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400"
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            <div className="sm:col-span-2">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                <RotateCcw size={13} />
                Reset Filters
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left">
              <thead className="bg-slate-50">
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3">ID</th>
                  <th className="px-4 py-3">Value</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {visibleItems.map((item, index) => (
                  <tr
                    key={item.id}
                    className="transition hover:bg-slate-50/70"
                  >
                    <td className="px-5 py-4 text-xs text-slate-500">
                      {String(item.id).padStart(3, "0")}
                    </td>

                    <td className="px-4 py-4">
                      <p className="text-xs font-semibold text-slate-800">
                        {item.value}
                      </p>
                    </td>

                    <td className="whitespace-nowrap px-4 py-4">
                      <button
                        type="button"
                        title="Toggle status"
                        onClick={() => toggleStatus(item)}
                        className="rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                      >
                        <StatusBadge status={item.status} />
                      </button>
                    </td>

                    <td className="whitespace-nowrap px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title="Edit value"
                          aria-label={`Edit ${item.value}`}
                          onClick={() => {
                            setEditingItem(item);
                            setModalOpen(true);
                          }}
                          className="rounded-lg p-2 text-amber-600 hover:bg-amber-50"
                        >
                          <Pencil size={15} />
                        </button>

                        <button
                          type="button"
                          title="Delete value"
                          aria-label={`Delete ${item.value}`}
                          onClick={() => setDeleteItem(item)}
                          className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {visibleItems.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-14 text-center">
                      <Database
                        size={28}
                        className="mx-auto text-slate-300"
                      />
                      <p className="mt-3 text-sm font-semibold text-slate-700">
                        No master values found
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Try another search or add a new value.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[11px] text-slate-500">
              Showing {filteredItems.length ? startIndex + 1 : 0} to{" "}
              {Math.min(startIndex + pageSize, filteredItems.length)} of{" "}
              {filteredItems.length} entries
            </p>

            <div className="flex items-center justify-between gap-3 sm:justify-end">
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                Show
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                  className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                </button>

                <span className="px-2 text-xs text-slate-600">
                  {safePage} / {totalPages}
                </span>

                <button
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() =>
                    setPage((p) => Math.min(totalPages, p + 1))
                  }
                  aria-label="Next page"
                  className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Add / Edit modal */}
      {modalOpen && (
        <ValueModal
          category={selectedCategory}
          item={editingItem}
          onClose={() => {
            setModalOpen(false);
            setEditingItem(null);
          }}
          onSave={saveItem}
        />
      )}

      {/* Delete confirmation */}
      {deleteItem && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
              <Trash2 size={22} />
            </div>

            <h3 className="mt-4 text-center text-lg font-bold text-slate-800">
              Delete Master Value?
            </h3>

            <p className="mt-2 text-center text-sm text-slate-500">
              Are you sure you want to delete{" "}
              <strong>{deleteItem.value}</strong> from{" "}
              <strong>{selectedCategory}</strong>?
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteItem(null)}
                className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
